import type {
  Erc20Diff,
  Erc721Diff,
  Erc1155Diff,
  NativeDiff,
  TransactionScanResponse,
  TransactionSimulation,
} from '@blockaid/client/resources/evm';
import type { MessageScanResponse } from '@blockaid/client/resources/solana/message';

import type { ChainKey } from '@infinex/chain-config';
import type { BlockaidValidationResult } from '@infinex/connect-service-contract/client';

// Type aliases for cleaner code
type SolanaResult = NonNullable<MessageScanResponse['result']>;
type SolanaSimulation = NonNullable<SolanaResult['simulation']>;
type SolanaAccountSummary = SolanaSimulation['account_summary'];
type SolanaAssetDiff = NonNullable<
  SolanaAccountSummary['account_assets_diff']
>[number];

export interface NormalizedAsset {
  type: 'native' | 'token' | 'nft';
  address?: string; // Optional for native assets
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  chainId?: string | number;
  chainName?: string;
}

export interface NormalizedAssetDiff {
  asset: NormalizedAsset;
  amount: string;
  usdValue: string;
  isReceiving: boolean;
  // NFT-specific fields
  tokenId?: string;
  isArbitraryToken?: boolean;
}

export interface NormalizedAssetApproval {
  asset: NormalizedAsset;
  amount: string;
  usdValue: string;
  spenderAddress: string;
}

export interface NormalizedValidationResult {
  assetDiffs: NormalizedAssetDiff[];
  assetApprovals: NormalizedAssetApproval[];
  totalUsdDiff?: {
    in: string;
    out: string;
    total: string;
  };
}

/**
 * Maps Solana asset type to normalized asset type
 */
function mapSolanaAssetType(
  asset: SolanaAssetDiff['asset']
): NormalizedAsset['type'] {
  if (!asset.type) return 'token';

  switch (asset.type) {
    case 'SOL':
    case 'ETH':
      return 'native';
    case 'TOKEN':
      return 'token';
    case 'NFT':
    case 'CNFT':
      return 'nft';
    default:
      return 'token';
  }
}

/**
 * Maps EVM asset type to normalized asset type
 */
function mapEvmAssetType(assetType: string): NormalizedAsset['type'] {
  switch (assetType) {
    case 'NATIVE':
      return 'native';
    case 'ERC20':
      return 'token';
    case 'ERC721':
    case 'ERC1155':
      return 'nft';
    default:
      return 'token';
  }
}

/**
 * Normalizes a Solana asset to the chain-agnostic structure
 */
function normalizeSolanaAsset(
  solAsset: SolanaAssetDiff['asset']
): NormalizedAsset {
  // Handle different asset types
  if ('symbol' in solAsset && 'name' in solAsset) {
    // Token or NFT asset
    return {
      type: mapSolanaAssetType(solAsset),
      address: solAsset.address,
      symbol: solAsset.symbol,
      name: solAsset.name,
      decimals: solAsset.decimals || 0,
      logoUrl: solAsset.logo || undefined,
      chainName: 'Solana',
    };
  } else {
    // Native asset (SOL)
    return {
      type: mapSolanaAssetType(solAsset),
      symbol: solAsset.type || 'SOL',
      name: solAsset.type || 'SOL',
      decimals: solAsset.decimals || 9,
      logoUrl: 'logo' in solAsset ? solAsset.logo || undefined : undefined,
      chainName: 'Solana',
    };
  }
}

/**
 * Normalizes an EVM asset to the chain-agnostic structure
 */
function normalizeEvmAsset(
  evmAsset: TransactionSimulation['assets_diffs'][string][number]['asset'],
  assetType: string
): NormalizedAsset {
  const baseAsset: NormalizedAsset = {
    type: mapEvmAssetType(assetType),
    symbol: evmAsset.symbol || 'Unknown',
    name: evmAsset.name || evmAsset.symbol || 'Unknown',
    decimals: 'decimals' in evmAsset ? evmAsset.decimals : 0,
  };

  // Handle type-specific fields
  if (assetType === 'NATIVE' && 'chain_name' in evmAsset) {
    baseAsset.chainName = evmAsset.chain_name;
    baseAsset.chainId = evmAsset.chain_id;
  } else if ('address' in evmAsset) {
    baseAsset.address = evmAsset.address;
  }

  if ('logo_url' in evmAsset) {
    baseAsset.logoUrl = evmAsset.logo_url;
  }

  return baseAsset;
}

/**
 * Normalizes a Solana simulation result to the chain-agnostic structure
 */
function normalizeSolanaResponse(
  solanaResult: MessageScanResponse
): NormalizedValidationResult {
  // Handle the full MessageScanResponse structure
  if (!solanaResult.result || !solanaResult.result.simulation) {
    return {
      assetDiffs: [],
      assetApprovals: [],
    };
  }

  const simulation = solanaResult.result.simulation;
  const accountSummary = simulation.account_summary;

  const assetDiffs: NormalizedAssetDiff[] = [];

  // Process asset diffs
  if (accountSummary.account_assets_diff) {
    accountSummary.account_assets_diff.forEach((diff) => {
      const normalizedAsset = normalizeSolanaAsset(diff.asset);

      // Add outgoing items
      if (diff.out) {
        assetDiffs.push({
          asset: normalizedAsset,
          amount: diff.out.value.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 16,
          }),
          usdValue: (diff.out.usd_price || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          isReceiving: false,
        });
      }

      // Add incoming items
      if (diff.in) {
        assetDiffs.push({
          asset: normalizedAsset,
          amount: diff.in.value.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 16,
          }),
          usdValue: (diff.in.usd_price || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          isReceiving: true,
        });
      }
    });
  }

  // Convert delegations to approvals
  const assetApprovals: NormalizedAssetApproval[] = [];

  if (accountSummary.account_delegations) {
    accountSummary.account_delegations.forEach((delegation) => {
      const normalizedAsset = normalizeSolanaAsset(delegation.asset);

      assetApprovals.push({
        asset: normalizedAsset,
        amount: delegation.delegation.value.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 16,
        }),
        usdValue: (delegation.delegation.usd_price || 0).toLocaleString(
          'en-US',
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        ),
        spenderAddress: delegation.delegate,
      });
    });
  }

  // Sort items: sending first (isReceiving: false), then receiving (isReceiving: true)
  const sortedAssetDiffs = assetDiffs.sort((a, b) => {
    if (!a.isReceiving && b.isReceiving) return -1;
    if (a.isReceiving && !b.isReceiving) return 1;
    return 0;
  });

  return {
    assetDiffs: sortedAssetDiffs,
    assetApprovals,
    totalUsdDiff: {
      in: accountSummary.total_usd_diff.in.toString(),
      out: accountSummary.total_usd_diff.out.toString(),
      total: (accountSummary.total_usd_diff.total || 0).toString(),
    },
  };
}

/**
 * Extracts the amount value from different types of EVM asset diffs
 */
function extractEvmAmount(
  diff: TransactionSimulation['account_summary']['assets_diffs'][number],
  item: Erc20Diff | Erc721Diff | Erc1155Diff | NativeDiff
): string {
  // Handle based on asset type
  if (diff.asset_type === 'ERC721') {
    // ERC721 tokens don't have an amount value, they're unique NFTs
    return '1';
  } else if (diff.asset_type === 'ERC1155') {
    // ERC1155 has a value property
    const value = (item as Erc1155Diff).value;
    // Format the number with locale string
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 16,
    });
  } else if (diff.asset_type === 'ERC20' || diff.asset_type === 'NATIVE') {
    // ERC20 and NATIVE have value (optional) or raw_value
    const assetItem = item as Erc20Diff | NativeDiff;
    if (assetItem.value) {
      // Format the number with locale string
      return parseFloat(assetItem.value).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 16,
      });
    }
    if ('raw_value' in assetItem && assetItem.raw_value) {
      // If we have raw_value but no value, convert hex to decimal
      if (assetItem.raw_value.startsWith('0x')) {
        try {
          const rawBigInt = BigInt(assetItem.raw_value);
          const asset = diff.asset as any;
          const decimals = 'decimals' in asset ? asset.decimals : 18; // Use asset decimals or default to 18

          // If decimals is 0, the raw value is the actual amount
          if (decimals === 0) {
            return rawBigInt.toString();
          }

          // Convert to decimal with proper decimals
          const divisor = BigInt(10 ** decimals);
          const wholePart = rawBigInt / divisor;
          const fractionalPart = rawBigInt % divisor;

          // Format the amount with proper decimal places
          const rawAmount =
            fractionalPart > 0n
              ? `${wholePart}.${fractionalPart.toString().padStart(decimals, '0').replace(/0+$/, '')}`
              : wholePart.toString();

          // Format with locale string for readability
          const parsedAmount = parseFloat(rawAmount);

          // Handle very small amounts
          if (parsedAmount > 0 && parsedAmount < 0.00001) {
            return '< 0.00001';
          }

          return parsedAmount.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 16,
          });
        } catch (e) {
          console.log('Error converting raw value to decimal:', e);
          // If conversion fails, return the raw value
          return assetItem.raw_value;
        }
      }
      return assetItem.raw_value;
    }
  }
  return '0';
}

/**
 * Normalizes an EVM simulation result to the chain-agnostic structure
 */
function normalizeEvmResponse(
  evmResult: TransactionScanResponse
): NormalizedValidationResult {
  // Handle both direct account_summary and nested simulation.account_summary
  let accountSummary: TransactionSimulation['account_summary'] | undefined;

  if (
    'simulation' in evmResult &&
    evmResult.simulation &&
    'status' in evmResult.simulation &&
    evmResult.simulation.status === 'Success' &&
    'account_summary' in evmResult.simulation
  ) {
    accountSummary = (evmResult.simulation as TransactionSimulation)
      .account_summary;
  }

  if (!accountSummary) {
    return {
      assetDiffs: [],
      assetApprovals: [],
    };
  }

  const accountAssetDiffs = accountSummary.assets_diffs;
  const accountExposures = accountSummary.exposures;

  const assetDiffs: NormalizedAssetDiff[] = [];

  // Process asset diffs from the user's account perspective
  if (accountAssetDiffs && accountAssetDiffs.length > 0) {
    accountAssetDiffs.forEach((diff) => {
      const normalizedAsset = normalizeEvmAsset(diff.asset, diff.asset_type);

      // Add outgoing items
      diff.out.forEach((outItem) => {
        const amount = extractEvmAmount(diff, outItem);
        const diffItem: NormalizedAssetDiff = {
          asset: normalizedAsset,
          amount,
          usdValue: outItem.usd_price
            ? parseFloat(outItem.usd_price).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '0.00',
          isReceiving: false,
        };

        // Add NFT-specific data
        if (diff.asset_type === 'ERC721' || diff.asset_type === 'ERC1155') {
          const nftItem = outItem as Erc721Diff | Erc1155Diff;
          diffItem.tokenId = nftItem.token_id;
          diffItem.isArbitraryToken = nftItem.arbitrary_collection_token;
        }

        assetDiffs.push(diffItem);
      });

      // Add incoming items
      diff.in.forEach((inItem) => {
        const amount = extractEvmAmount(diff, inItem);
        const diffItem: NormalizedAssetDiff = {
          asset: normalizedAsset,
          amount,
          usdValue: inItem.usd_price
            ? parseFloat(inItem.usd_price).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '0.00',
          isReceiving: true,
        };

        // Add NFT-specific data
        if (diff.asset_type === 'ERC721' || diff.asset_type === 'ERC1155') {
          const nftItem = inItem as Erc721Diff | Erc1155Diff;
          diffItem.tokenId = nftItem.token_id;
          diffItem.isArbitraryToken = nftItem.arbitrary_collection_token;
        }

        assetDiffs.push(diffItem);
      });
    });
  }

  // Process exposures (approvals) from the user's account perspective
  const assetApprovals: NormalizedAssetApproval[] = [];

  // Exposures is an object where keys are addresses and values are arrays of exposures
  if (accountExposures && accountExposures.length > 0) {
    accountExposures.forEach((exposure) => {
      const normalizedAsset = normalizeEvmAsset(
        exposure.asset,
        exposure.asset_type
      );

      // For each spender in the exposure
      Object.entries(exposure.spenders).forEach(
        ([spenderAddress, spenderData]) => {
          // Use the approval amount, not the exposure amount
          if (spenderData.approval) {
            // Check if it's infinite approval
            // Common max values used in approvals:
            // - max uint256: used by standard ERC20 approvals
            // - max uint160: used by Permit2 (Uniswap)
            // - max uint96: used by some older protocols
            const maxUint256 = BigInt(
              '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
            );
            const maxUint160 = BigInt(
              '0xffffffffffffffffffffffffffffffffffffffff'
            );
            const maxUint96 = BigInt('0xffffffffffffffffffffffff');
            const approvalBigInt = BigInt(spenderData.approval);

            // Consider it infinite if it's exactly a max uint value or greater than 10^30
            // Lowered threshold from 10^50 to 10^30 to catch Permit2's uint160 max (~1.46e48)
            // while still being far above any realistic token amount
            const infiniteThreshold = BigInt('1000000000000000000000000000000'); // 10^30
            const isInfiniteApproval =
              approvalBigInt === maxUint256 ||
              approvalBigInt === maxUint160 ||
              approvalBigInt === maxUint96 ||
              approvalBigInt > infiniteThreshold;

            let amount: string;
            let usdValue: string;

            if (isInfiniteApproval) {
              amount = 'Unlimited';
              usdValue = 'Unlimited';
            } else {
              // Convert hex approval to decimal with proper decimals
              const decimals = normalizedAsset.decimals;
              const divisor = BigInt(10 ** decimals);
              const wholePart = approvalBigInt / divisor;
              const fractionalPart = approvalBigInt % divisor;

              // Format the amount with proper decimal places
              const rawAmount =
                fractionalPart > 0n
                  ? `${wholePart}.${fractionalPart.toString().padStart(decimals, '0').replace(/0+$/, '')}`
                  : wholePart.toString();

              // Format with locale string for readability
              amount = parseFloat(rawAmount).toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 16,
              });

              // Use approval_usd_price if available, otherwise calculate
              if (
                spenderData.approval_usd_price ===
                '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
              ) {
                usdValue = 'Unlimited';
              } else if (spenderData.approval_usd_price) {
                // Convert hex to decimal if it's a hex string
                let usdPriceValue: number;
                if (
                  typeof spenderData.approval_usd_price === 'string' &&
                  spenderData.approval_usd_price.startsWith('0x')
                ) {
                  // Convert hex to BigInt then to decimal with proper decimals
                  const hexValue = BigInt(spenderData.approval_usd_price);
                  // USD values typically have 6 decimals in the hex representation
                  const divisor = BigInt(10 ** 6);
                  const wholePart = hexValue / divisor;
                  const fractionalPart = hexValue % divisor;
                  usdPriceValue =
                    Number(wholePart) + Number(fractionalPart) / 1000000;
                } else {
                  usdPriceValue = parseFloat(spenderData.approval_usd_price);
                }

                usdValue = usdPriceValue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              } else {
                usdValue = '0.00';
              }
            }

            assetApprovals.push({
              asset: normalizedAsset,
              amount,
              usdValue,
              spenderAddress,
            });
          }
        }
      );
    });
  }

  // Sort items: sending first (isReceiving: false), then receiving (isReceiving: true)
  const sortedAssetDiffs = assetDiffs.sort((a, b) => {
    if (!a.isReceiving && b.isReceiving) return -1;
    if (a.isReceiving && !b.isReceiving) return 1;
    return 0;
  });

  return {
    assetDiffs: sortedAssetDiffs,
    assetApprovals,
    totalUsdDiff: accountSummary?.total_usd_diff,
  };
}

/**
 * Normalizes chain-specific validation responses to a common chain-agnostic format
 */
export function normalizeChainValidationResponse(params: {
  chainKey: ChainKey;
  txnValidationResult: BlockaidValidationResult | null;
}): NormalizedValidationResult {
  const { chainKey, txnValidationResult } = params;

  if (!txnValidationResult) {
    return {
      assetDiffs: [],
      assetApprovals: [],
    };
  }

  if (chainKey === 'solana') {
    // It's a Solana response that needs normalization
    return normalizeSolanaResponse(txnValidationResult as MessageScanResponse);
  } else {
    // It's an EVM response (ethereum, arbitrum, optimism, etc.)
    return normalizeEvmResponse(txnValidationResult as TransactionScanResponse);
  }
}
